import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import { sortingOrder } from '../../../Utils/sortingOrder';
import { axiosInstance } from '../../../../config/axiosInstance';
import { globalEnv } from '../../../../config/baseConfig';
import moment from 'moment';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import TableLoadingSkeleton from '../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import Pagination from '../../../Tables/pagination';
import { HeaderItem } from '../../../Tables/TablesList/Components/common';
import { useDispatch } from 'react-redux';
import { setTab } from '../../../../redux/actions/sideBarAction';

export default function CampaignList() {
	const [campaignsList, setCampaignsList] = useState();
	const [selectedHeader, setSelectedHeader] = useState('createdAt');

	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const { data, isError, isFetching, isLoading } = useQuery(
		[page, searchQuery, sortOrder],
		() => fetchCampaigns(),
		{
			cacheTime: 0,
			keepPreviousData: false,
			staleTime: 5000,
			refetchOnWindowFocus: false
		}
	);

	const fetchCampaigns = async () => {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/outreach/campaign?page=${page}&sortby=${'createdAt'}&order=${sortOrder}${
					searchQuery !== ''
				}`,
			config
		);
		return data;
	};

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Campaign name"
					slugName="name"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Sent emails count"
					slugName="sentEmails"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Contacted Users count"
					slugName="usersCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Keywords"
					slugName="Keywords"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Update date"
					slugName="updatedAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
			</tr>
		);
	};
	const dispatch = useDispatch();
	dispatch(setTab('outreach'));

	return (
		<section className="pl-16 pt-16">
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data.campaigns.map((item: any) => (
										<TableData
											key={item._id}
											id={item._id}
											name={item.name}
											sentEmailsCount={item.sentEmails}
											contactedUsersCount={item.contactedUsers}
											keywords={item.keywords}
											createdAt={item.createdAt}
											updatedAt={item.updatedAt}
											// image={
											// 	item.coverPic != null
											// 		? item.coverPic
											// 		: item.backgroundImage
											// }
										/>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>
		</section>
	);
}

type tableDateType = {
	id: string;
	name: string;
	image?: any;
	createdAt: string;
	updatedAt: string;
	sentEmailsCount: number;
	contactedUsersCount: number;
	keywords?: string;
};

const TableData = ({
	id,
	name,
	createdAt,
	updatedAt,
	sentEmailsCount,
	contactedUsersCount,
	keywords
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../outreach/campaign/${id}`);
			}}
		>
			<td className="col-span-1 p-2 break-all">
				<div className="flex items-center">
					{/* <div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								image != null
									? image.url_low_res != null
										? image.url_low_res
										: placeholder
									: placeholder
							}
							style={
								'block object-cover object-center w-full aspect-[1/1] rounded-lg'
							}
						/>
					</div> */}
					<div className="font-medium text-gray-800 dark:text-white">
						{name}
					</div>
				</div>
			</td>

			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{sentEmailsCount}</div>
			</td>

			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">
					{contactedUsersCount}
				</div>
			</td>

			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{keywords?.length}</div>
			</td>

			<td className="p-2 whitespace-nowrap w-20">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(createdAt).format('MM/DD/YYYY')}
				</div>
			</td>

			<td className="p-2 whitespace-nowrap w-20">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(updatedAt).format('MM/DD/YYYY')}
				</div>
			</td>
		</tr>
	);
};
